import { motion } from 'motion/react';
import { Building2, ChevronDown } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { InstitutionalHoldersFileResponse } from '@/services/api';
import {
  buildLatestQuarterTopHolders,
  formatInstitutionalChartShares,
  formatInstitutionalMarketValueUsd,
  type TopInstitutionalHolderRow,
} from '@/utils/institutionalHoldersChartData';

function positionBadgeClass(label: string | null): string {
  switch (label?.toLowerCase()) {
    case 'calls':
      return 'bg-sky-50 text-sky-700 ring-sky-100';
    case 'puts':
      return 'bg-amber-50 text-amber-700 ring-amber-100';
    default:
      return 'bg-slate-100 text-slate-600 ring-slate-200/60';
  }
}

function HolderRow({
  holder,
  rank,
  selected,
  onSelect,
}: {
  holder: TopInstitutionalHolderRow;
  rank: number;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        title={holder.fullName}
        onClick={onSelect}
        className={`group grid w-full grid-cols-[1.25rem_1fr_2.75rem_3.25rem] items-start gap-x-2 px-3.5 py-2.5 text-left transition-colors ${
          selected ? 'bg-emerald-50/60' : 'hover:bg-slate-50/90'
        }`}
      >
        <span
          className={`pt-px text-[11px] font-medium tabular-nums ${
            selected ? 'text-emerald-600' : 'text-slate-300 group-hover:text-slate-400'
          }`}
        >
          {rank}
        </span>

        <span className="min-w-0">
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-[13px] font-medium leading-snug text-slate-900">
              {holder.institutionName}
            </span>
            {holder.positionTypeLabel ? (
              <span
                className={`shrink-0 rounded px-1 py-px text-[8px] font-semibold uppercase tracking-wide ring-1 ring-inset ${positionBadgeClass(holder.positionTypeLabel)}`}
              >
                {holder.positionTypeLabel}
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block truncate text-[10px] tabular-nums text-slate-400">
            {formatInstitutionalChartShares(holder.shares)} shares
          </span>
        </span>

        <span className="pt-px text-right text-[11px] font-medium tabular-nums text-slate-700">
          {holder.ownership > 0 ? `${holder.ownership.toFixed(2)}%` : '—'}
        </span>

        <span className="pt-px text-right text-[11px] tabular-nums text-slate-500">
          {holder.marketValue > 0 ? formatInstitutionalMarketValueUsd(holder.marketValue) : '—'}
        </span>
      </button>
    </li>
  );
}

export function InstitutionalHoldersTopHoldersWidget({
  layerActive,
  payload,
  loading,
  loadError,
  selectedPositionKey = null,
  onSelectHolder,
}: {
  layerActive: boolean;
  payload: InstitutionalHoldersFileResponse | null;
  loading: boolean;
  loadError: string | null;
  selectedPositionKey?: string | null;
  onSelectHolder: (quarterKey: string, positionKey: string, displayName: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const topHolders = useMemo(() => buildLatestQuarterTopHolders(payload, 50), [payload]);

  useEffect(() => {
    if (!layerActive) setOpen(false);
  }, [layerActive]);

  useEffect(() => {
    if (!layerActive || !open) return;
    const onPointerDown = (e: PointerEvent) => {
      const t = e.target;
      if (!(t instanceof Node)) return;
      if (panelRef.current?.contains(t)) return;
      const el = t instanceof Element ? t : null;
      if (el?.closest('[data-inst-top-holders-dismiss-skip="true"]')) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [layerActive, open]);

  if (!layerActive) return null;

  if (!open) {
    return (
      <motion.button
        type="button"
        data-inst-top-holders-dismiss-skip="true"
        initial={{ opacity: 0, y: 6 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.18 }}
        onClick={() => setOpen(true)}
        className="absolute right-3 bottom-12 z-[36] flex items-center gap-2 rounded-full border border-slate-200/80 bg-white/95 px-3.5 py-1.5 text-[11px] font-medium text-slate-700 shadow-[0_2px_12px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-colors hover:border-slate-300 hover:bg-white"
        aria-label="Show largest institutional holders"
      >
        <Building2 className="h-3.5 w-3.5 text-slate-500" aria-hidden />
        Top holders
      </motion.button>
    );
  }

  return (
    <motion.div
      ref={panelRef}
      initial={{ opacity: 0, y: 6, scale: 0.98 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 6, scale: 0.98 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      className="absolute right-3 bottom-12 z-[36] w-[19.5rem] overflow-hidden rounded-xl border border-slate-200/80 bg-white/98 shadow-[0_8px_30px_rgba(15,23,42,0.10)] backdrop-blur-md"
    >
      <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-3.5 py-3">
        <div className="min-w-0">
          <p className="text-[13px] font-medium tracking-tight text-slate-900">Top holders</p>
          {topHolders ? (
            <p className="mt-0.5 text-[11px] text-slate-500">
              {topHolders.snapshotPeriod} 13F
              <span className="mx-1.5 text-slate-300">·</span>
              {topHolders.quarterLabel}
            </p>
          ) : (
            <p className="mt-0.5 text-[11px] text-slate-400">Latest filing</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="shrink-0 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          aria-label="Collapse top holders panel"
        >
          <ChevronDown className="h-4 w-4" />
        </button>
      </div>

      {!loading && !loadError && topHolders && topHolders.holders.length > 0 ? (
        <div className="grid grid-cols-[1.25rem_1fr_2.75rem_3.25rem] gap-x-2 border-b border-slate-50 px-3.5 py-1.5 text-[9px] font-medium uppercase tracking-wider text-slate-400">
          <span />
          <span>Institution</span>
          <span className="text-right">Own</span>
          <span className="text-right">Value</span>
        </div>
      ) : null}

      <div className="institutional-holders-ribbon-scroll max-h-[min(48vh,360px)] overflow-y-auto">
        {loading && (
          <div className="flex items-center justify-center gap-2 py-8">
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-slate-200 border-t-slate-500" />
            <span className="text-xs text-slate-400">Loading…</span>
          </div>
        )}
        {!loading && loadError && (
          <p className="px-3.5 py-6 text-center text-xs text-rose-500">{loadError}</p>
        )}
        {!loading && !loadError && !topHolders && (
          <p className="px-3.5 py-6 text-center text-xs text-slate-400">No holder data yet.</p>
        )}
        {!loading && !loadError && topHolders && topHolders.holders.length === 0 && (
          <p className="px-3.5 py-6 text-center text-xs text-slate-400">No open positions filed.</p>
        )}
        {!loading && !loadError && topHolders && topHolders.holders.length > 0 && (
          <ul className="divide-y divide-slate-100/90">
            {topHolders.holders.map((holder, idx) => (
              <HolderRow
                key={holder.positionKey}
                holder={holder}
                rank={idx + 1}
                selected={selectedPositionKey === holder.positionKey}
                onSelect={() =>
                  onSelectHolder(
                    topHolders.quarterKey,
                    holder.positionKey,
                    holder.positionTypeLabel
                      ? `${holder.institutionName} (${holder.positionTypeLabel})`
                      : holder.institutionName
                  )
                }
              />
            ))}
          </ul>
        )}
      </div>
    </motion.div>
  );
}
